import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { AuthenticationService } from '@bo-client/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { ChangePasswordModalComponent } from '@bo-client/core/change-password-modal/change-password-modal.component';
import { takeUntil } from 'rxjs/operators';
import { UserMenu, UserMenuActionEnum } from '@bo-client/core/navigation/user-menu.definition';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationService } from '@bo-client/core/navigation/navigation.service';
import { path } from '@bo-client/shared/constants/path';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { BuildUpToDateService } from '@bo-client/core/api/build-up-to-date.service';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { LoginDialogComponent } from '@bo-client/shared/login/login-dialog/login-dialog.component';
import { EditPersonalDataModalComponent } from '@bo-client/shared/login/edit-personal-data-modal/edit-personal-data-modal.component';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';
import { UserBackOfficeSettingsModel } from '@bo-client/shared/models/userBackOfficeSettings';
import { EditPersonalizationModalComponent } from '@bo-client/shared/login/edit-personalization-modal/edit-personalization-modal.component';
import { AppNewsViewerService } from '@bo-client/shared/app-news/app-news-viewer.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav: MatSidenav;
  public userMenu = UserMenu;
  public hideLoginMenu = false;
  public buildIsUpToDate = true;
  private compUnsubs$ = new Subject<void>();

  constructor(
    private router: Router,
    public authenticationService: AuthenticationService,
    private navigationService: NavigationService,
    private toastService: ToastrService,
    public dataShareService: DataShareService,
    public matDialog: MatDialog,
    private authorizationService: AuthorizationService,
    upToDateService: BuildUpToDateService,
    private userConfigService: UserConfigService,
    private appNewsViewerService: AppNewsViewerService
  ) {
    upToDateService.buildIsUpToDate.pipe(takeUntil(this.compUnsubs$)).subscribe((buildIsUpToDate) => {
      this.buildIsUpToDate = buildIsUpToDate;
    });
  }

  get username(): string | null {
    const credentials = this.authenticationService.credentials;
    return credentials ? credentials.username : null;
  }

  ngOnInit() {
    this.hideLoginMenu = this.router.url.includes(path.external);
    // Immer aus der vollständigen Definition filtern und auch auf neue Benutzerdaten reagieren:
    // isLoggedIn$ meldet sich beim Login, bevor die Rollen geladen sind, und ein rollenabhängiger
    // Eintrag (z. B. „Neuigkeiten") muss nach einem Nutzerwechsel ohne Reload wieder erscheinen.
    combineLatest([this.authenticationService.isLoggedIn$, this.dataShareService.userData$])
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe(() => {
        this.userMenu = this.navigationService.generateMenu(UserMenu);
      });
  }

  login() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = 'login-tm-dialog';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    this.matDialog.open(LoginDialogComponent, dialogConfig);
  }

  logout() {
    this.authenticationService.logout().subscribe((result) => {
      this.router
        .navigate([this.authorizationService.getLogoutRoute()], {
          replaceUrl: true
        })
        .finally(() => {
          this.dataShareService.logout();
        });
    });
  }

  changePassword() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'change-password';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = this.dataShareService.userData$; // TODO: pass the id's of the current user
    this.matDialog
      .open(ChangePasswordModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((result) => {
        if (result === true) {
          this.toastService.success('Passwort erfolgreich geändert.');
        }
      });
  }

  editUserProfile() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'edit-user-profile';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    this.matDialog
      .open(EditPersonalDataModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((result) => {
        if (result) {
          //this.dataStore.setUser(result);
          this.toastService.success('Profil erfolgreich bearbeitet.');
        }
      });
  }

  changePersonalization() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'edit-personalization';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    this.matDialog
      .open(EditPersonalizationModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((result: Partial<UserBackOfficeSettingsModel>) => {
        if (result) {
          this.userConfigService.userSettings.updateMultiple(result);
          this.toastService.success('Anpassungen wurden gespeichert.');
        }
      });
  }

  clickOnMenu($event: UserMenuActionEnum) {
    switch ($event) {
      case UserMenuActionEnum.changePassword:
        this.changePassword();
        break;
      case UserMenuActionEnum.changeProfile:
        this.editUserProfile();
        break;
      case UserMenuActionEnum.logout:
        this.logout();
        break;
      case UserMenuActionEnum.changePersonalization:
        this.changePersonalization();
        break;
      case UserMenuActionEnum.appNews:
        this.appNewsViewerService.openFromMenu();
        break;
    }
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }

  reload() {
    location.reload();
  }
}
